import { useState, useEffect, useCallback } from 'react';
import { collection, doc, onSnapshot, query, orderBy, limit, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const PEEK_LIMIT = 50;

// Tracks unread chat messages for the signed-in user.
// "Unread" = messages from someone else, posted after the user's last
// recorded visit to the Chat screen (users/{uid}.lastReadChatAt).
//
// Returns:
//   unreadCount  — number of messages newer than lastReadChatAt, capped
//                  at PEEK_LIMIT (anything beyond shows as "50+")
//   markRead()   — call when the user opens the Chat screen
//   capped       — true if there are more unread than we counted
export function useChatUnread(user) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [capped, setCapped] = useState(false);
  const [lastReadMs, setLastReadMs] = useState(null);

  // Subscribe to user doc → read lastReadChatAt
  useEffect(() => {
    if (!user) { setLastReadMs(null); return; }
    return onSnapshot(doc(db, 'users', user.uid), (snap) => {
      const ts = snap.exists() ? snap.data().lastReadChatAt : null;
      setLastReadMs(ts?.toMillis ? ts.toMillis() : 0);
    });
  }, [user]);

  // Subscribe to recent chat messages, count those after lastReadMs (and not from this user)
  useEffect(() => {
    if (!user || lastReadMs === null) { setUnreadCount(0); setCapped(false); return; }
    const q = query(collection(db, 'chat'), orderBy('createdAt', 'desc'), limit(PEEK_LIMIT));
    return onSnapshot(q, (snap) => {
      let count = 0;
      snap.forEach((d) => {
        const m = d.data();
        if (m.uid === user.uid) return;
        const ms = m.createdAt?.toMillis?.();
        if (!ms) return; // pending server timestamp
        if (ms > lastReadMs) count++;
      });
      setUnreadCount(count);
      setCapped(snap.size === PEEK_LIMIT && count === PEEK_LIMIT);
    });
  }, [user, lastReadMs]);

  const markRead = useCallback(async () => {
    if (!user) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), { lastReadChatAt: serverTimestamp() });
    } catch (err) {
      console.warn('[useChatUnread] failed to mark read:', err);
    }
  }, [user]);

  return { unreadCount, markRead, capped };
}
